// mu — the sync seam: peer status, the ambient hook, and the two power
// tools behind `mu sync`.
//
// WHAT LIVES HERE AND WHAT DOES NOT
// ---------------------------------
// `src/segments.ts` owns the MECHANISM (flush / ingest / watermarks /
// the four robustness layers). This file owns the OPERATOR-FACING
// questions that mechanism cannot answer on its own:
//
//   - who are my peers, how far behind am I, when did I last hear from
//     them (`peerStatuses`)
//   - the AMBIENT hook every mu invocation runs (`ambientIngest` /
//     `ambientFlush`)
//   - a DIFFERENT READER: a peer's `ops` table read straight out of a
//     copied / sshfs-mounted `mu.db` (`ingestFromDb`)
//   - the universal repair: reset a watermark and re-read from zero
//     (`repairPeer`)
//
// AMBIENT, NOT A DAEMON
// ---------------------
// There is no watcher, no background process, and no polling loop that
// outlives the command. Sync happens because you already run `mu`
// constantly: every invocation ingests before the verb body and flushes
// after it. With `MU_SYNC_DIR` unset both halves are a single `if` that
// reads one env var, so the single-machine case pays nothing. With 2-5
// peers it is a handful of `stat`/`read` calls on small append-only
// files.
//
// The TUI is the one long-lived surface, and it hangs the same pass off
// its SLOW tick (10s), never the 1s fast tick — sync is filesystem work
// and has no business on a repaint cadence.
//
// SYNC MUST NEVER FAIL A COMMAND
// ------------------------------
// A truncated segment, an unreadable sync dir, a full disk: all of them
// warn and return. `mu task add` has to work when sync is broken,
// because the alternative is a tool that stops working when a folder you
// do not control misbehaves. Every ambient entry point here is
// try/catch-total by construction (see `ambientIngest`).
//
// TRANSPORT STAYS THE OPERATOR'S
// ------------------------------
// mu reads and writes FILES. It does not shell out to ssh/scp/rsync, and
// it will not grow `--push <host>`: that is a remote backend wearing a
// small hat (ssh config, jump hosts, ProxyCommand, ports, identity
// files, interactive prompts inside a TUI tick, network-vs-auth error
// mapping), and it violates the standing ROADMAP pledge that the user
// owns transport. `mu sync` reports a stale peer (`stale: true`); moving
// its segment (Syncthing, rsync, a USB stick) is the operator's.

import { existsSync, statSync } from "node:fs";
import Database from "better-sqlite3";
import type { Op } from "./apply.js";
import { CURRENT_SCHEMA_VERSION, type Db, SYNCED_ENTITIES } from "./db.js";
import { say } from "./messages.js";
import { isJsonMode } from "./output.js";
import {
  applyIncomingOp,
  discoverPeers,
  type FlushResult,
  flushSegment,
  getWatermark,
  type IngestResult,
  ingestSegment,
  localMachineId,
  peerWatermarkKey,
  resetWatermark,
  type SegmentDefect,
  segmentCaughtUp,
  segmentLineCount,
  syncDir,
} from "./segments.js";

/** No discovered peer matches the operator's `--repair <peer>` ref.
 *  Exit 3 (not found), like every other resolve-time miss. */
export class SyncPeerNotFoundError extends Error {
  override readonly name = "SyncPeerNotFoundError";
  constructor(
    readonly ref: string,
    readonly known: readonly string[],
  ) {
    super(say("sync.peer_not_found", ref, known));
  }
}

/** A `--repair <peer>` prefix matched several peers. A conflict (exit 4),
 *  never a guess — the same rule `mu log --group` follows for
 *  abbreviated group ids. */
export class SyncPeerRefAmbiguousError extends Error {
  override readonly name = "SyncPeerRefAmbiguousError";
  constructor(
    readonly ref: string,
    readonly candidates: readonly string[],
  ) {
    super(say("sync.peer_ambiguous", ref, candidates));
  }
}

/** `mu sync --from <path>` pointed at a file that is not there. Exit 3. */
export class SyncSourceNotFoundError extends Error {
  override readonly name = "SyncSourceNotFoundError";
  constructor(readonly path: string) {
    super(say("sync.source_missing", path));
  }
}

/** `mu sync --from <db>` pointed at a DB written by a newer mu. Its ops
 *  may carry fields this build would half-apply, which is exactly what a
 *  segment line's version prevents; this path has no such line. Exit 4. */
export class SyncSourceTooNewError extends Error {
  override readonly name = "SyncSourceTooNewError";
  constructor(
    readonly path: string,
    readonly detectedVersion: number,
  ) {
    super(say("sync.source_too_new", path, detectedVersion, CURRENT_SCHEMA_VERSION));
  }
}

/** A peer whose segment has not moved in this long is reported STALE.
 *  A fixed constant, not an env var: it is a display threshold, and mu's
 *  whole cluster configuration is deliberately ONE env var. */
export const PEER_STALE_MS = 24 * 60 * 60 * 1000;

/** How many characters of a `machine_id` uuid we show. Peers are known
 *  only by uuid — `machine_identity.hostname` is machine-LOCAL and never
 *  ships, so mu genuinely cannot render a peer's hostname without
 *  inventing a membership file. A short prefix is the honest display,
 *  and every verb that takes one accepts any unique prefix (the
 *  affordance git gives for shas). */
export const PEER_SHORT_LEN = 8;

export interface PeerStatus {
  machineId: string;
  /** First `PEER_SHORT_LEN` chars of the machine id, for display. */
  short: string;
  path: string;
  /** True for a Syncthing-style `*.sync-conflict-*.jsonl` copy. */
  conflictCopy: boolean;
  /** `sync_peers` key of this file's watermark (see `PeerSegment`).
   *  `peerStatuses` always sets it. Optional so SDK callers that build
   *  the pre-watermarkKey shape still compile; absent means the machine
   *  id (`peerWatermarkKey`). */
  watermarkKey?: string;
  /** Lines of this segment file already applied. */
  watermark: number;
  /** GOOD lines currently in the segment (a defect stops the count). */
  total: number;
  /** `total - watermark`: how much of what we HOLD is not yet applied.
   *  Non-zero after a defect stopped an ingest short, or mid-transfer. */
  behind: number;
  /** Segment mtime in epoch ms — when transport last delivered. */
  lastSeenMs: number | null;
  /** Age of the segment file, ms. Null when it does not exist. */
  ageMs: number | null;
  stale: boolean;
}

/** Peer table for the sync dir, newest contact first. Pure read: it
 *  neither flushes nor ingests, so `mu sync` can call it after its own
 *  pass and the TUI could call it on a tick. */
export function peerStatuses(db: Db, dir: string): PeerStatus[] {
  const self = localMachineId(db);
  const now = Date.now();
  const rows = discoverPeers(dir, self).map((peer) => {
    let lastSeenMs: number | null = null;
    try {
      lastSeenMs = statSync(peer.path).mtimeMs;
    } catch {
      lastSeenMs = null;
    }
    const watermarkKey = peerWatermarkKey(peer);
    const watermark = getWatermark(db, watermarkKey);
    // Caught up: the count is the watermark, no need to decode the file.
    const total = segmentCaughtUp(db, watermarkKey, peer.path, watermark)
      ? watermark
      : segmentLineCount(peer.path);
    const ageMs = lastSeenMs === null ? null : Math.max(0, now - lastSeenMs);
    return {
      machineId: peer.machineId,
      short: peer.machineId.slice(0, PEER_SHORT_LEN),
      path: peer.path,
      conflictCopy: peer.conflictCopy,
      watermarkKey,
      watermark,
      total,
      behind: Math.max(0, total - watermark),
      lastSeenMs,
      ageMs,
      stale: ageMs === null || ageMs > PEER_STALE_MS,
    };
  });
  return rows.sort((a, b) => (b.lastSeenMs ?? 0) - (a.lastSeenMs ?? 0));
}

/** Resolve an operator-typed peer reference (full machine id, or any
 *  unique prefix) against the discovered peers. Ambiguity is a
 *  UsageError, never a guess — repairing the wrong peer would re-read a
 *  whole segment for nothing and confuse the report.
 *
 *  A ref names a MACHINE, so a segment and its conflict copies (same
 *  machine id) count as one match; the peer's own segment is returned
 *  when present. Counting them separately made the short id `mu sync`
 *  prints unresolvable. */
export function resolvePeerRef(peers: readonly PeerStatus[], ref: string): PeerStatus {
  const matches = peers.filter((p) => p.machineId.startsWith(ref));
  const exact = matches.filter((p) => p.machineId === ref);
  const candidates = exact.length > 0 ? exact : matches;
  const machines = [...new Set(candidates.map((p) => p.machineId))];
  const first = candidates.find((p) => !p.conflictCopy) ?? candidates[0];
  if (first === undefined) {
    throw new SyncPeerNotFoundError(
      ref,
      peers.map((p) => p.short),
    );
  }
  if (machines.length > 1) {
    throw new SyncPeerRefAmbiguousError(
      ref,
      machines.map((m) => m.slice(0, PEER_SHORT_LEN)),
    );
  }
  return first;
}

/** Reset the watermark of every segment file a peer has (its own and
 *  any conflict copies) so the next ingest re-reads them from zero. Safe
 *  by construction: apply is idempotent and `ops` dedupes on
 *  `UNIQUE (machine_id, hlc)`. */
export function repairPeer(db: Db, ref: string, dir: string): PeerStatus {
  const peers = peerStatuses(db, dir);
  const peer = resolvePeerRef(peers, ref);
  for (const p of peers) {
    if (p.machineId === peer.machineId) resetWatermark(db, peerWatermarkKey(p));
  }
  return peer;
}

// ─── the ambient hook ─────────────────────────────────────────────────

/** Is sync configured at all? THE single `if` the no-sync case pays.
 *  Callers check this before awaiting anything, so an unconfigured
 *  machine allocates no promise and touches no filesystem. */
export function syncEnabled(): boolean {
  return syncDir() !== null;
}

export interface AmbientResult {
  /** Peers whose segments were read. Empty when sync is off. */
  ingested: readonly IngestResult[];
  /** Non-fatal problems, already warned about. */
  warnings: readonly string[];
}

export interface AmbientOptions {
  /** Suppress the stderr warnings. Set by the TUI, which owns the
   *  alternate screen — a stray write there paints garbage over the
   *  dashboard. The warnings are still returned: the dashboard keeps the
   *  latest ones as `sync` attention items until the next ingest. */
  quiet?: boolean;
}

export interface AmbientFlushOptions extends AmbientOptions {
  /** How long to wait for the segment lock (default
   *  AMBIENT_FLUSH_LOCK_WAIT_MS). On timeout nothing is written; the ops
   *  stay in the DB and the next flush appends them. */
  lockWaitMs?: number;
}

/** Format one non-fatal sync problem for stderr. Prefixed so it is
 *  unmistakably mu's own aside and not the verb's output. */
function warn(message: string, opts?: AmbientOptions): void {
  if (opts?.quiet === true) return;
  // --json: stderr stays empty; the ops stay pending and `mu doctor` reports the backlog.
  if (isJsonMode()) return;
  // Deliberately stderr: stdout is the verb's contract (and may be JSON).
  process.stderr.write(`mu: sync: ${message}\n`);
}

/** One line for an own segment holding a line of a version this build
 *  cannot read (the segment is read-only here until mu is upgraded).
 *  Shared by `mu sync`, the ambient warning and `mu doctor`. */
export function foreignVersionMessage(r: {
  segmentPath: string | null;
  foreignVersion: number | null;
}): string {
  return `segment ${r.segmentPath ?? "?"} has a v${r.foreignVersion ?? "?"} line written by a newer mu; upgrade mu on this machine`;
}

function describeDefects(peerShort: string, defects: readonly SegmentDefect[]): string {
  const first = defects[0];
  const detail = first === undefined ? "unknown defect" : `${first.kind} at line ${first.line}`;
  const more = defects.length > 1 ? ` (+${defects.length - 1} more)` : "";
  // `--repair` only resets the watermark, so it is advice ONLY for a
  // defect a re-read can get past (a torn write mid-transfer). A refused
  // line is deterministic: re-reading hits the identical line and the
  // hint would be self-referentially useless, which is exactly how the
  // original incident's operator was sent in a circle. A re-delivered
  // op is deterministic for the same reason. So is a manifest mismatch:
  // the file itself is short, re-reading it changes nothing, and only a
  // fresh copy from the peer clears it.
  const skipped = new Set(["entity-not-synced", "duplicate-op"]);
  const fileWide = new Set(["manifest-mismatch"]);
  let hint: string;
  if (defects.every((d) => skipped.has(d.kind))) {
    hint = " — skipped; the rest of the segment applied";
  } else if (defects.every((d) => skipped.has(d.kind) || fileWide.has(d.kind))) {
    hint = " — the file is shorter than its manifest; copy it from the peer again";
  } else {
    hint = ` — re-read with \`mu sync --repair ${peerShort}\``;
  }
  return `peer ${peerShort}: ${detail}${more}${hint}`;
}

/**
 * INGEST half of the ambient hook: pull every peer segment before the
 * verb body runs, so the command sees the freshest state the filesystem
 * can offer.
 *
 * Total: no input can make this throw. A per-peer failure is isolated so
 * one broken segment cannot hide the others.
 */
export async function ambientIngest(db: Db, opts?: AmbientOptions): Promise<AmbientResult> {
  const dir = syncDir();
  if (dir === null) return { ingested: [], warnings: [] };
  const warnings: string[] = [];
  const ingested: IngestResult[] = [];
  try {
    const self = localMachineId(db);
    for (const peer of discoverPeers(dir, self)) {
      try {
        const result = ingestSegment(db, peer);
        ingested.push(result);
        if (result.defects.length > 0) {
          const message = describeDefects(peer.machineId.slice(0, PEER_SHORT_LEN), result.defects);
          warnings.push(message);
          warn(message, opts);
        }
      } catch (err) {
        const message = `peer ${peer.machineId.slice(0, PEER_SHORT_LEN)}: ${err instanceof Error ? err.message : String(err)}`;
        warnings.push(message);
        warn(message, opts);
      }
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    warnings.push(message);
    warn(message, opts);
  }
  // `await` nothing above: ingest is synchronous better-sqlite3 work.
  // The async signature exists so the caller's seam is uniform with
  // ambientFlush (which takes a file lock).
  return { ingested, warnings };
}

/**
 * FLUSH half of the ambient hook: append this invocation's own ops to
 * this machine's segment AFTER the verb body has committed them.
 *
 * Order matters and is the whole reason the hook is split in two: a
 * flush before the body would leave the ops the operator just wrote
 * sitting unflushed until the NEXT invocation, so `mu task add` on the
 * laptop followed by `mu sync` on the devserver would show nothing —
 * exactly the no-hands claim, broken.
 *
 * Appends under the cross-process file lock inside `flushSegment`, so
 * two concurrent mu processes cannot interleave partial lines. A flush
 * with nothing to append skips the lock, so read-only verbs never queue.
 * A flush that cannot take the lock within AMBIENT_FLUSH_LOCK_WAIT_MS
 * writes nothing (`lockBusy`) and the verb carries on (exit 0) after one
 * stderr line: its ops stay pending in `ops` and the next invocation's
 * flush appends them. `mu doctor` reports the backlog (`unpublished-ops`).
 */
export async function ambientFlush(
  db: Db,
  opts?: AmbientFlushOptions,
): Promise<FlushResult | null> {
  const dir = syncDir();
  if (dir === null) return null;
  try {
    const result = await flushSegment(db, dir, { lockWaitMs: opts?.lockWaitMs });
    if (result.selfRepaired !== null) {
      const d = result.selfRepaired;
      warn(
        `own segment truncated back to its last good line: ${d.kind} at line ${d.line} (${d.detail}) — regenerated from the ops table`,
        opts,
      );
    }
    if (result.foreignVersionAt !== null) warn(foreignVersionMessage(result), opts);
    if (result.lockBusy === true) {
      warn("segment locked by another mu; this op publishes on the next flush", opts);
    }
    return result;
  } catch (err) {
    warn(err instanceof Error ? err.message : String(err), opts);
    return null;
  }
}

/**
 * Both halves in one call, for a caller that has no before/after seam to
 * straddle — today the TUI's SLOW tick (10s; never the 1s fast tick,
 * which is a repaint cadence and has no business doing filesystem work).
 *
 * Ingest first, then flush: the same order the CLI hook uses, so a
 * long-lived TUI converges on exactly the same schedule as a shell that
 * runs one verb every ten seconds.
 */
export async function ambientSyncPass(
  db: Db,
  opts?: AmbientOptions,
): Promise<{ ingested: readonly IngestResult[]; flushed: FlushResult | null }> {
  if (!syncEnabled()) return { ingested: [], flushed: null };
  const { ingested } = await ambientIngest(db, opts);
  const flushed = await ambientFlush(db, opts);
  return { ingested, flushed };
}

// ─── `mu sync --from <peer.db>`: a different READER ───────────────────

export interface IngestFromDbResult {
  path: string;
  /** Ops read out of the peer's `ops` table (already filtered). */
  read: number;
  /** Ops that changed a row here. */
  changed: number;
  /** Ops skipped because their entity is machine-local. */
  skippedLocal: number;
}

/** The source DB's `schema_version`, or null when it has none (not a mu
 *  DB, or one old enough that the ops query below fails on its own). */
function sourceSchemaVersion(source: Database.Database): number | null {
  try {
    const row = source.prepare("SELECT version FROM schema_version WHERE id = 1").get() as
      | { version: number }
      | undefined;
    return row?.version ?? null;
  } catch {
    return null;
  }
}

/**
 * Ingest straight from a peer's `mu.db`, reading its `ops` table with
 * SQLite instead of parsing a JSONL segment.
 *
 * WHY THIS EARNS A FLAG when `MU_SYNC_DIR=/mnt/whatever mu state` covers
 * the one-off-directory case: it is a DIFFERENT READER, and nothing
 * about an env var can express it. The file you have is a database, not
 * a segment — because you scp'd it, or because you have the devserver's
 * state dir on sshfs and would rather read the real thing than wait for
 * a flush.
 *
 * Opened `readonly` so mu cannot write to a file it does not own, and
 * `fileMustExist` so a typo'd path is an error rather than a freshly
 * created empty DB.
 *
 * Filters exactly as flush does — `SYNCED_ENTITIES` only, and never the
 * peer's copy of OUR ops (we already have those; `UNIQUE (machine_id,
 * hlc)` would dedupe them anyway, but not reading them is cheaper and
 * keeps the reported count honest). Ops the peer itself ingested from a
 * THIRD machine are read, which is how transitive convergence falls out
 * for free.
 *
 * Watermarks are deliberately untouched: a watermark counts LINES of a
 * segment, and a DB has no such coordinate. The next segment ingest for
 * that peer re-reads from wherever it was, applies the same ops again,
 * and changes nothing — idempotence is what makes leaving it alone safe.
 */
export function ingestFromDb(db: Db, path: string): IngestFromDbResult {
  if (!existsSync(path)) throw new SyncSourceNotFoundError(path);
  const self = localMachineId(db);
  const source = new Database(path, { readonly: true, fileMustExist: true });
  try {
    const theirs = sourceSchemaVersion(source);
    if (theirs !== null && theirs > CURRENT_SCHEMA_VERSION) {
      throw new SyncSourceTooNewError(path, theirs);
    }
    const rows = source
      .prepare(
        `SELECT hlc, machine_id, group_id, actor, intent, entity, key, op, payload
           FROM ops
          WHERE machine_id != ?
          ORDER BY hlc`,
      )
      .all(self) as Array<{
      hlc: string;
      machine_id: string;
      group_id: string;
      actor: string | null;
      intent: string | null;
      entity: string;
      key: string;
      op: string;
      payload: string;
    }>;

    const synced = new Set<string>(SYNCED_ENTITIES);
    let read = 0;
    let changed = 0;
    let skippedLocal = 0;
    const run = db.transaction(() => {
      for (const row of rows) {
        if (!synced.has(row.entity)) {
          skippedLocal += 1;
          continue;
        }
        const op: Op = {
          hlc: row.hlc,
          machineId: row.machine_id,
          groupId: row.group_id,
          actor: row.actor,
          intent: row.intent,
          entity: row.entity,
          key: row.key,
          op: row.op === "del" ? "del" : "put",
          payload: row.payload,
        };
        const result = applyIncomingOp(db, op);
        read += 1;
        if (result.changed) changed += 1;
      }
    });
    run.immediate();
    return { path, read, changed, skippedLocal };
  } finally {
    try {
      source.close();
    } catch {
      // best effort
    }
  }
}
